import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import styles from './MapWidget.module.css';
import mapboxgl from "../../common/mapbox";

// Country configuration
const COUNTRY_CONFIG = {
  CH: {
    center: [8.2275, 46.8182],
    zoom: 6.1,
    iso: 'CHE'
  },
  DE: {
    center: [10.4515, 51.1657],
    zoom: 4.6,
    iso: 'DEU'
  },
  AT: {
    center: [13.4501, 47.5162],
    zoom: 6.2,
    iso: 'AUT'
  }
};

// Activity type colors
const ACTIVITY_COLORS = {
  ACTIVE_IN_PERIOD: '#028A0F',    // ral-green.500
  INACTIVE_IN_PERIOD: '#FAD201',  // ral-yellow.500
  NEVER_ACTIVE: '#CC0605'         // ral-red.500
};

/**
 * MapWidget Component
 *
 * A React component that renders an interactive Mapbox map with location markers
 * Features:
 * - Clustering of nearby markers
 * - Popup tooltips on hover
 * - Customizable styling
 * - Multi-country support (Switzerland, Germany, Austria)
 * - Activity-based marker coloring
 */
const MapWidget = ({
                     locations = [],
                     countryCode = 'CH',
                     countryCodes = [],
                     accessToken = process.env.REACT_APP_MAPBOX_KEY,
                     height = '500px',
                     showLegend = true,
                     mapStyle = 'mapbox://styles/mapbox/light-v11'
                   }) => {
  // Refs for map container and map instance
  const mapContainer = useRef(null);
  const popupRef = useRef(null);
  const map = useRef(null);
  const { t } = useTranslation();

  // Component state
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const selectedCountryCodes = (countryCodes && countryCodes.length > 0) ? countryCodes : [countryCode];
  const selectedCountryConfigs = selectedCountryCodes.map(code => COUNTRY_CONFIG[code]).filter(Boolean);
  const selectedIsos = selectedCountryConfigs.map(cfg => cfg.iso);

  // Get country configuration
  const countryConfig = COUNTRY_CONFIG[selectedCountryCodes[0]] || COUNTRY_CONFIG.CH;

  const fitToSelectedCountries = () => {
    if (!map.current) return;

    if (selectedCountryConfigs.length <= 1) {
      const cfg = selectedCountryConfigs[0] || COUNTRY_CONFIG.CH;
      map.current.easeTo({
        center: cfg.center,
        zoom: cfg.zoom
      });
      return;
    }

    const bounds = new mapboxgl.LngLatBounds();
    selectedCountryConfigs.forEach(cfg => bounds.extend(cfg.center));

    map.current.fitBounds(bounds, {
      padding: 80,
      duration: 800
    });
  };

  // Main map initialization effect
  useEffect(() => {
    // Validate access token
    if (!accessToken) {
      setError('Please provide a valid Mapbox access token.');
      return;
    }

    mapboxgl.accessToken = accessToken;

    // Initialize popup instance
    if (!popupRef.current) {
      popupRef.current = new mapboxgl.Popup({
        offset: 15,
        closeButton: false,
        closeOnClick: true,
        closeOnMove: false
      });
    }

    // Initialize map if not already created
    if (!map.current && mapContainer.current) {
      try {
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: mapStyle,
          center: countryConfig.center,
          zoom: countryConfig.zoom,
          projection: 'globe'
        });

        // Add navigation controls (zoom in/out, reset bearing)
        map.current.addControl(
            new mapboxgl.NavigationControl({
              showCompass: false
            }),
            'top-right'
        );

        // Set up map layers and data sources when map loads
        map.current.on('load', () => {
          setupMapLayers();
          setupEventHandlers();
          setLoaded(true);
          fitToSelectedCountries();
        });

        // Handle map errors
        map.current.on('error', (e) => {
          console.error('Map error:', e);
          setError('Map loading error. Please check your access token and internet connection.');
        });

      } catch (err) {
        console.error('Error initializing map:', err);
        setError('Error initializing map: ' + err.message);
      }
    }

    // Cleanup function
    return () => {
      if (popupRef.current) {
        try {
          popupRef.current.remove();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
      if (map.current) {
        try {
          map.current.remove();
          map.current = null;
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [countryCode, countryCodes]);

  useEffect(() => {
    if (!map.current || !loaded) return;

    if (map.current.getLayer('countries-fill-except-selected')) {
      map.current.setFilter('countries-fill-except-selected', ['!', ['in', ['get', 'iso_3166_1_alpha_3'], ['literal', selectedIsos]]]);
    }

    if (map.current.getLayer('selected-country-border')) {
      map.current.setFilter('selected-country-border', ['in', ['get', 'iso_3166_1_alpha_3'], ['literal', selectedIsos]]);
    }

    fitToSelectedCountries();
  }, [countryCodes, loaded]);

  // Update locations when locations prop changes
  useEffect(() => {
    if (map.current && loaded && locations) {
      updateLocationsData();
    }
  }, [locations, loaded]);

  /**
   * Sets up map layers for countries and location markers
   */
  const setupMapLayers = () => {
    try {
      // Add country boundaries source
      map.current.addSource('countries', {
        type: 'vector',
        url: 'mapbox://mapbox.country-boundaries-v1'
      });

      // Add faded background for all countries
      map.current.addLayer({
        id: 'countries-faded',
        type: 'fill',
        source: 'countries',
        'source-layer': 'country_boundaries',
        paint: {
          'fill-color': '#e4e4e4',
          'fill-opacity': 0.1
        }
      });

      // Add country border lines
      map.current.addLayer({
        id: 'countries-borders-faded',
        type: 'line',
        source: 'countries',
        'source-layer': 'country_boundaries',
        paint: {
          'line-color': '#c0c0c0',
          'line-width': 0.9
        }
      });

      // Fill all countries except the selected one with gray
      map.current.addLayer({
        id: 'countries-fill-except-selected',
        type: 'fill',
        source: 'countries',
        'source-layer': 'country_boundaries',
        filter: ['!', ['in', ['get', 'iso_3166_1_alpha_3'], ['literal', selectedIsos]]],
        paint: {
          'fill-color': '#e4e4e4',
          'fill-opacity': 0.7
        }
      });

      // Highlight selected country border
      map.current.addLayer({
        id: 'selected-country-border',
        type: 'line',
        source: 'countries',
        'source-layer': 'country_boundaries',
        filter: ['in', ['get', 'iso_3166_1_alpha_3'], ['literal', selectedIsos]],
        paint: {
          'line-color': '#c0c0c0',
          'line-width': 2
        }
      });

      // Add locations data source with clustering
      map.current.addSource('locations', {
        type: 'geojson',
        data: createGeoJSONFromLocations(),
        cluster: true,
        clusterMaxZoom: 14, // Max zoom to cluster points on
        clusterRadius: 50   // Radius of each cluster when clustering points
      });

      // Add cluster circles - ALL CLUSTERS NOW USE PRIMARY BLUE COLOR
      map.current.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'locations',
        filter: ['has', 'point_count'],
        paint: {
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20, // Default size
            3, 22, // 3+ points: size 22
            5, 25  // 5+ points: size 25
          ],
          'circle-color': '#005A8C', // ALL CLUSTERS USE PRIMARY BLUE COLOR
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff'
        }
      });

      // Add cluster count text
      map.current.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'locations',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        },
        paint: {
          'text-color': '#ffffff'
        }
      });

      // Add individual unclustered points - COLOR BASED ON ACTIVITY TYPE
      map.current.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'locations',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'case',
            ['==', ['get', 'activityType'], 'ACTIVE_IN_PERIOD'], ACTIVITY_COLORS.ACTIVE_IN_PERIOD,
            ['==', ['get', 'activityType'], 'INACTIVE_IN_PERIOD'], ACTIVITY_COLORS.INACTIVE_IN_PERIOD,
            ['==', ['get', 'activityType'], 'NEVER_ACTIVE'], ACTIVITY_COLORS.NEVER_ACTIVE,
            ACTIVITY_COLORS.NEVER_ACTIVE // Default fallback
          ],
          'circle-radius': 8,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff'
        }
      });

      // Add labels for individual points
      map.current.addLayer({
        id: 'location-labels',
        type: 'symbol',
        source: 'locations',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-size': 11,
          'text-offset': [0, 1.5],
          'text-anchor': 'top'
        },
        paint: {
          'text-color': '#404040',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1
        }
      });

    } catch (err) {
      console.error('Error setting up map layers:', err);
      setError('Error setting up map layers: ' + err.message);
    }
  };

  /**
   * Sets up event handlers for map interactions
   */
  const setupEventHandlers = () => {
    // Click handler for clusters - zooms in to expand cluster
    map.current.on('click', 'clusters', (e) => {
      const features = map.current.queryRenderedFeatures(e.point, { layers: ['clusters'] });
      const clusterId = features[0].properties.cluster_id;

      map.current.getSource('locations').getClusterExpansionZoom(
          clusterId,
          (err, zoom) => {
            if (err) return;

            map.current.easeTo({
              center: features[0].geometry.coordinates,
              zoom: zoom
            });
          }
      );
    });

    // Hover handlers for individual points
    map.current.on('mouseenter', 'unclustered-point', handleMarkerHover);
    map.current.on('mouseleave', 'unclustered-point', handleMarkerLeave);

    // Cursor changes for clusters
    map.current.on('mouseenter', 'clusters', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', 'clusters', () => {
      map.current.getCanvas().style.cursor = '';
    });
  };

  /**
   * Handles marker hover to show popup
   */
  const handleMarkerHover = (e) => {
    if (!map.current || !popupRef.current) return;

    map.current.getCanvas().style.cursor = 'pointer';

    const coordinates = e.features[0].geometry.coordinates.slice();
    const { id, name, number, url, driveOffCount } = e.features[0].properties;

    // Create popup content
    const popupContent = createPopupContent(name, number, url, driveOffCount);

    // Handle longitude wrapping
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    // Remove existing popup and show new one
    if (popupRef.current.isOpen()) {
      popupRef.current.remove();
    }

    popupRef.current
        .setLngLat(coordinates)
        .setDOMContent(popupContent)
        .addTo(map.current);
  };

  /**
   * Handles marker mouse leave to hide popup
   */
  const handleMarkerLeave = () => {
    if (!map.current) return;

    map.current.getCanvas().style.cursor = '';

    // Delay hiding popup to allow hover over popup itself
    setTimeout(() => {
      if (!popupRef.current || !popupRef.current.isOpen()) return;

      const popupElement = popupRef.current.getElement();
      if (popupElement && !popupElement.matches(':hover')) {
        popupRef.current.remove();
      }
    }, 200);
  };

  /**
   * Creates popup content DOM element
   */
  const createPopupContent = (name, number, url, driveOffCount, activityType) => {
    const popupContent = document.createElement('div');
    popupContent.className = styles.markerPopup;

    // Get activity status text
    const getActivityStatusText = (type) => {
      switch(type) {
        case 'ACTIVE_IN_PERIOD':
          return t("Reports.Dashboard.ActivityStatus.ActiveInPeriod") || 'Active in Period';
        case 'INACTIVE_IN_PERIOD':
          return t("Reports.Dashboard.ActivityStatus.InactiveInPeriod") || 'Inactive in Period';
        case 'NEVER_ACTIVE':
          return t("Reports.Dashboard.ActivityStatus.NeverActive") || 'Never Active';
        default:
          return type;
      }
    };

    // Get activity status color
    const getActivityStatusColor = (type) => {
      return ACTIVITY_COLORS[type] || ACTIVITY_COLORS.NEVER_ACTIVE;
    };

    popupContent.innerHTML = `
      ${number ? `<p class="${styles.popupText}"><strong>${t("Reports.Dashboard.GasStationID") || "ID"}:</strong> ${number}</p>` : ''}
      ${driveOffCount !== undefined ? `<p class="${styles.popupText}"><strong>${t("Reports.Dashboard.DOCount") || "Drive Offs"}:</strong> ${driveOffCount}</p>` : ''}
      ${url ? `<a href="${url}" target="_blank" rel="noopener noreferrer" class="${styles.popupLink}">Visit website</a>` : ''}
    `;

    // Handle popup mouse leave
    popupContent.addEventListener('mouseleave', () => {
      setTimeout(() => {
        if (popupRef.current && popupRef.current.isOpen()) {
          const popupElement = popupRef.current.getElement();
          if (popupElement && !popupElement.matches(':hover')) {
            popupRef.current.remove();
          }
        }
      }, 100);
    });

    return popupContent;
  };

  /**
   * Creates GeoJSON feature collection from locations array
   */
  const createGeoJSONFromLocations = () => {
    return {
      type: 'FeatureCollection',
      features: locations.map(location => ({
        type: 'Feature',
        properties: {
          id: location.id,
          name: location.name,
          number: location.number,
          type: location.type,
          url: location.url,
          driveOffCount: location.driveOffCount,
          activityType: location.activityType
        },
        geometry: {
          type: 'Point',
          coordinates: location.coordinates
        }
      }))
    };
  };

  /**
   * Updates the locations data source with new locations
   */
  const updateLocationsData = () => {
    const source = map.current.getSource('locations');
    if (source) {
      source.setData(createGeoJSONFromLocations());
    }
  };

  // Render error state
  if (error) {
    return (
        <div
            className={styles.errorContainer}
            style={{ height }}
        >
          <div className={styles.errorContent}>
            <p className={styles.errorTitle}>❌ Map Error</p>
            <p className={styles.errorMessage}>{error}</p>
          </div>
        </div>
    );
  }

  // Render main component
  return (
      <div className={styles.mapContainer} style={{ height }}>
        <div ref={mapContainer} className={styles.mapRef} />

        {/* Loading overlay */}
        {!loaded && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingContent}>
                <div className={styles.spinner}></div>
                <p>Loading map...</p>
              </div>
            </div>
        )}

        {/* Legend */}
        {showLegend && loaded && (
            <div className={styles.legend}>
              <h4 className={styles.legendTitle}>{t("Reports.Dashboard.Legend")}</h4>
              <div className={styles.legendItem}>
                <div className={styles.legendMarker} style={{ backgroundColor: ACTIVITY_COLORS.ACTIVE_IN_PERIOD }}></div>
                <span>{t("Reports.Dashboard.ActivityType.ACTIVE_IN_PERIOD") || "Active in Period"}</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendMarker} style={{ backgroundColor: ACTIVITY_COLORS.INACTIVE_IN_PERIOD }}></div>
                <span>{t("Reports.Dashboard.ActivityType.INACTIVE_IN_PERIOD") || "Inactive in Period"}</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendMarker} style={{ backgroundColor: ACTIVITY_COLORS.NEVER_ACTIVE }}></div>
                <span>{t("Reports.Dashboard.ActivityType.NEVER_ACTIVE") || "Never Active"}</span>
              </div>
            </div>
        )}
      </div>
  );
};

export default MapWidget;